import { useState } from "react";
import { Logo } from "../Logo";
import { ConnectChoiceMenu } from "./ConnectChoiceMenu";
import { useConnectIntegration } from "../../../hooks/useConnectIntegration";
import type { RequiredApp } from "../../../lib/requiredApps";
import type { Integration } from "../../../types/integration";
import styles from "./RequiredAppsChecklist.module.css";

type Props = {
  apps: ReadonlyArray<RequiredApp>;
  tenantId: string | null;
};

// The apps an admin pinned to this customer's invite, shown while the
// instance is still locked. Connecting happens in a NEW tab (OAuth where we
// can start it, the integration's page otherwise) so working through several
// apps never costs them this screen.
export const RequiredAppsChecklist = ({ apps, tenantId }: Props): JSX.Element | null => {
  const startConnect = useConnectIntegration(tenantId);
  const [busy, setBusy] = useState<string | null>(null);

  if (apps.length === 0) return null;

  const handleConnect = async (slug: string, integration: Integration): Promise<void> => {
    setBusy(slug);
    try {
      await startConnect(slug, integration);
    } finally {
      setBusy(null);
    }
  };

  const connectedCount = apps.filter((a) => a.connected).length;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3 className={styles.heading}>Connect these apps</h3>
        <span className={styles.progress}>
          {connectedCount} of {apps.length} connected
        </span>
      </div>
      <ul className={styles.list}>
        {apps.map((app) => (
          <li key={app.token} className={styles.row}>
            <div className={styles.main}>
              {app.prompt !== null ? (
                // A choice group is not any one of its members, so it must not
                // wear a member's logo — "Connect your email" beside the Gmail
                // mark reads as a Gmail requirement.
                <span className={styles.groupMark} aria-hidden="true">
                  ✉
                </span>
              ) : (
                <Logo
                  slug={app.options[0]?.slug ?? app.token}
                  size={24}
                  brandHex={app.options[0]?.integration.iconBrandHex ?? null}
                  logoUrl={
                    app.options[0]?.integration.ourLogoUrl ??
                    app.options[0]?.integration.logoUrl ??
                    null
                  }
                />
              )}
              <span className={styles.name}>{app.prompt ?? app.title}</span>
              {app.connected ? (
                <span className={`${styles.state} ${styles.connected}`}>
                  <span className={styles.check} aria-hidden="true">✓</span> Connected
                </span>
              ) : !app.actionable ? (
                <span className={`${styles.state} ${styles.managed}`}>We&apos;ll set this up</span>
              ) : app.prompt !== null ? (
                <ConnectChoiceMenu
                  prompt={app.prompt}
                  choices={app.options.filter((o) => o.integration.configured)}
                  busySlug={busy}
                  onChoose={(choice) => void handleConnect(choice.slug, choice.integration)}
                />
              ) : app.options[0] !== undefined ? (
                <ConnectButton
                  title={app.options[0].title}
                  busy={busy === app.options[0].slug}
                  onClick={() => {
                    const only = app.options[0];
                    if (only !== undefined) void handleConnect(only.slug, only.integration);
                  }}
                />
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

type ConnectButtonProps = {
  title: string;
  busy: boolean;
  onClick: () => void;
};

const ConnectButton = ({ title, busy, onClick }: ConnectButtonProps): JSX.Element => (
  <button
    type="button"
    className={styles.connectBtn}
    disabled={busy}
    aria-label={`Connect ${title}`}
    onClick={onClick}
  >
    {busy ? "Opening…" : "Connect"}
  </button>
);
